import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Link, useLocation } from 'react-router-dom';
import '../assets/styles/TradingEdge.css';
import '../assets/styles/EnhancedTradingEdge.css';
import '../assets/styles/VibeDefinition.css';

const TradingEdge: React.FC = () => {
  const location = useLocation();
  
  // Handle auto-scrolling to hash fragments like #video-demo
  useEffect(() => {
    // Check if hash exists
    if (location.hash) {
      // Remove the leading # character
      const id = location.hash.substring(1);
      
      // Find the element with that id
      const targetElement = document.getElementById(id);
      
      // If the element exists, scroll to it
      if (targetElement) {
        // Use setTimeout to ensure DOM is fully rendered
        setTimeout(() => {
          targetElement.scrollIntoView({ behavior: 'smooth' });
        }, 300); // Increased timeout for better reliability
      }
    } else {
      // If no hash, scroll to top (redundant with global ScrollToTop but ensures consistency)
      window.scrollTo(0, 0);
    }
  }, [location]); // Re-run when location changes

  // Image paths - Import from assets folder with relative paths
  const pnlDistributionPath = process.env.PUBLIC_URL + '/images/reports/pnl_distribution.png';
  const cumulativePerformancePath = process.env.PUBLIC_URL + '/images/reports/cumulative_pnl_overtime.png';
  const winRatePerHourPath = process.env.PUBLIC_URL + '/images/reports/winrate_per_hour_of_day.png';
  
  // Fallback to existing statistical chart if reports are not found
  const statisticalEdgeChart = process.env.PUBLIC_URL + '/images/reports/pnl_x_tradeduration.png';

  return (
    <div className="trading-edge-page">
      <Helmet>
        <title>AI Vibe Trading: The Ultimate Trading Edge | Translate Stocks</title>
        <meta name="description" content="Discover how Agentic AI Vibe Trading, our Agentic AI bot, trades alongside you using natural language interaction with charts, delivering a 78% win rate for intraday trading." />
      </Helmet>

      <section className="hero-banner">
        <div className="container">
          <div className="hero-content">
            <span className="overline">PATENTED PROPRIETARY TECHNOLOGY</span>
            <h1>
              <span className="highlight">AI Vibe Trading</span> with <span className="highlight">78% Win Rate</span> in Every Trade
            </h1>
            <p className="hero-subtitle">
              Our Agentic AI bot trades alongside you, interacting with charts through natural language while our Price on Rails technology delivers precision analytics with a measurable statistical edge that other traders miss.
            </p>
            <div className="hero-cta">
              <Link to="/pricing" className="btn btn-primary btn-large">
                Start Vibe Trading Now
              </Link>
              <Link to="/demo" className="btn btn-outline btn-large">
                See It In Action
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="vibe-trading-definition">
        <div className="container">
          <div className="definition-content">
            <div className="definition-header">
              <h2>What is <span className="highlight">AI Vibe Trading</span>?</h2>
            </div>
            <div className="definition-body">
              <p className="definition-text">
                <strong>AI Vibe Trading</strong> is our patent-protected Agentic AI bot that trades alongside you and interacts directly with your charts using natural language. 
                Just like "vibe coding" lets AI handle programming complexities, Vibe Trading is your AI trading partner that processes millions of data points while you communicate in plain English.
                Our Price on Rails technology provides real-time price projections with a documented 78% win rate across all market conditions.
              </p>
              <div className="proof-points">
                <div className="proof-item">
                  <div className="proof-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18456 2.99721 7.13638 4.39828 5.49707C5.79935 3.85776 7.69279 2.71793 9.79619 2.24013C11.8996 1.76233 14.1003 1.98003 16.07 2.86" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M22 4L12 14.01L9 11.01" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <p><strong>Natural Language Trading</strong> - Trade by simply telling the AI what you want to do in plain English</p>
                </div>
                <div className="proof-item">
                  <div className="proof-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18456 2.99721 7.13638 4.39828 5.49707C5.79935 3.85776 7.69279 2.71793 9.79619 2.24013C11.8996 1.76233 14.1003 1.98003 16.07 2.86" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M22 4L12 14.01L9 11.01" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <p><strong>Adaptive AI Agent</strong> - The bot learns your trading style and adapts to your preferences</p>
                </div>
                <div className="proof-item">
                  <div className="proof-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18456 2.99721 7.13638 4.39828 5.49707C5.79935 3.85776 7.69279 2.71793 9.79619 2.24013C11.8996 1.76233 14.1003 1.98003 16.07 2.86" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M22 4L12 14.01L9 11.01" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </div>
                  <p><strong>Chart Interaction</strong> - The AI interacts directly with your charts, recognizing patterns and suggesting trades</p>
                </div>
              </div>
              <div className="testimonial-banner">
                <blockquote>
                  "AI Vibe Trading represents the next evolution in AI-enhanced trading. It's like having a professional trading assistant that you can talk to, working alongside you to identify opportunities others miss."
                  <cite>— Munir Antum, Early Investor</cite>
                </blockquote>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="edge-highlights">
        <div className="container">
          <div className="section-header">
            <h2>The Secret <span className="highlight">Vibe Formula</span> Top Traders Don't Want You to Know</h2>
            <p>
              AI Vibe Trading isn't just another trading approach – it's the synthesis of market mathematics, artificial intelligence, and real-time analytics that eliminates guesswork from trading while you trade on intuition.
            </p>
          </div>

          <div className="highlight-grid">
            <div className="highlight-card">
              <div className="highlight-icon">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M24 4L8 14V34L24 42L40 34V14L24 4Z" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M8 14L24 24M24 24L40 14M24 24V42" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>AI-Powered Future Projection</h3>
              <p>
                Our proprietary AI constantly analyzes <strong>every second of market data</strong> to predict price movements before they happen, drawing the likely future price path directly on your chart.
              </p>
            </div>

            <div className="highlight-card">
              <div className="highlight-icon">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M42 32C42 33.0609 41.5786 34.0783 40.8284 34.8284C40.0783 35.5786 39.0609 36 38 36H10C8.93913 36 7.92172 35.5786 7.17157 34.8284C6.42143 34.0783 6 33.0609 6 32V16C6 14.9391 6.42143 13.9217 7.17157 13.1716C7.92172 12.4214 8.93913 12 10 12H38C39.0609 12 40.0783 12.4214 40.8284 13.1716C41.5786 13.9217 42 14.9391 42 16V32Z" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 28L22 22L28 28L36 20" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M32 20H36V24" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Statistical Confluence Points</h3>
              <p>
                While others guess, Price on Rails identifies <strong>statistical extremes</strong> with mathematical precision, the exact moments when multiple indicators align to create high-probability reversal points.
              </p>
            </div>

            <div className="highlight-card">
              <div className="highlight-icon">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M24 44C35.0457 44 44 35.0457 44 24C44 12.9543 35.0457 4 24 4C12.9543 4 4 12.9543 4 24C4 35.0457 12.9543 44 24 44Z" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M24 12V24L32 28" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Perfect Trade Timing</h3>
              <p>
                Most traders lose by entering too early or too late. Price on Rails delivers <strong>precise entry and exit signals</strong> when statistically significant price levels are reached, eliminating emotional mistakes.
              </p>
            </div>

            <div className="highlight-card">
              <div className="highlight-icon">
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M44 24H36L30 44L18 4L12 24H4" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Volatility Intelligence</h3>
              <p>
                While most traders are blindsided by sudden moves, Price on Rails <strong>anticipates volatility changes</strong> and adjusts targets automatically, maximizing profit potential even in turbulent markets.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="statistical-edge">
        <div className="container">
          <div className="edge-content">
            <div className="edge-text">
              <span className="overline">MATHEMATICAL ADVANTAGE</span>
              <h2>Agentic AI Trading With <span className="highlight">Statistical Confidence</span></h2>
              <div className="patent-badge">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M20 12H21" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3 12H4" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 20V21" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 3V4" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M17.657 17.6569L18.364 18.364" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M5.63599 5.63605L6.34309 6.34315" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M17.657 6.34315L18.364 5.63605" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M5.63599 18.364L6.34309 17.6569" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Patent Protected Technology</span>
              </div>
              <p>
                Most traders operate on gut feeling, but AI Vibe Trading revolutionizes this approach. While you trade on intuition, our Price on Rails technology leverages proven statistical methods behind the scenes, giving you a significant edge in the market with our advanced AI delivering exceptional win rates.
              </p>
              
              <div className="confidence-levels">
                <div className="confidence-item">
                  <div className="confidence-bar" style={{ width: '50%' }}>
                    <span>50% - 60%</span>
                  </div>
                  <p>Average Trading System</p>
                </div>
                
                <div className="confidence-item">
                  <div className="confidence-bar" style={{ width: '60%' }}>
                    <span>60% - 70%</span>
                  </div>
                  <p>Good Trading System <a href="https://tgrafi.co/ibhycqe" target="_blank" rel="noopener noreferrer">*</a></p>
                </div>
                
                <div className="confidence-item highlight">
                  <div className="confidence-bar" style={{ width: '80%' }}>
                    <span>78% - 83%</span>
                  </div>
                  <p>Price on Rails</p>
                </div>
              </div>
              
              <div className="edge-quote">
                <blockquote>
                  "The difference between amateur and professional traders isn't luck, it's statistical edge. AI Vibe Trading with Price on Rails gives you that professional edge."
                </blockquote>
              </div>
            </div>
            
            <div className="statistical-edge-image">
              <img 
                src={statisticalEdgeChart} 
                alt="Price on Rails statistical edge demonstration" 
                width="540" 
                height="400"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.onerror = null;
                  target.src = process.env.PUBLIC_URL + '/images/reports/pnl_x_tradeduration.png';
                }}
              />
              <p className="image-caption">Visual demonstration of Price on Rails' statistical advantage.</p>
            </div>
          </div>
        </div>
      </section>

      
      <section className="real-trading-data">
        <div className="container">
          <div className="section-header center">
            <h2>Real <span className="highlight">Trading Data</span> From Real Accounts</h2>
            <p>
              These aren't just claims - here's the actual data from our trading analysis reports. These statistics come from real trading accounts using Price on Rails technology.
            </p>
          </div>
          
          <div className="report-highlights">
            <div className="report-card">
              <div className="report-image">
                <img 
                  src={winRatePerHourPath} 
                  alt="78% Win Rate from actual trading report" 
                  loading="lazy"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.onerror = null;
                    target.src = process.env.PUBLIC_URL + '/images/reports/pnl_x_tradeduration.png';
                  }}
                />
                <div className="verification-badge">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 12L11 14L15 10" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#4CAF50" strokeWidth="2"/>
                  </svg>
                  <span>Verified Data</span>
                </div>
              </div>
              <h3>78% Win Rate</h3>
              <p>
                Our system consistently delivers a 78% win rate across different market conditions, as verified by actual trading accounts.
              </p>
            </div>
            
            <div className="report-card">
              <div className="report-image">
                <img 
                  src={pnlDistributionPath} 
                  alt="PnL distribution from actual trading report" 
                  loading="lazy"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.onerror = null;
                    target.src = process.env.PUBLIC_URL + '/images/reports/pnl_x_tradeduration.png';
                  }}
                />
                <div className="verification-badge">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 12L11 14L15 10" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#4CAF50" strokeWidth="2"/>
                  </svg>
                  <span>Verified Data</span>
                </div>
              </div>
              <h3>Positive PnL Distribution</h3>
              <p>
                The profit distribution shows a clear positive skew, with the average trade producing 1.36 points of profit.
              </p>
            </div>
            
            <div className="report-card">
              <div className="report-image">
                <img 
                  src={cumulativePerformancePath} 
                  alt="Cumulative performance from actual trading report" 
                  loading="lazy"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.onerror = null;
                    target.src = process.env.PUBLIC_URL + '/images/reports/pnl_x_tradeduration.png';
                  }}
                />
                <div className="verification-badge">
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M9 12L11 14L15 10" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="#4CAF50" strokeWidth="2"/>
                  </svg>
                  <span>Verified Data</span>
                </div>
              </div>
              <h3>Consistent Growth</h3>
              <p>
                The equity curve shows steady growth over time, with minimal drawdowns and consistent positive performance.
              </p>
            </div>
          </div>
          
          <div className="report-note">
            <p>
              <strong>Note:</strong> These statistics come directly from our trading analysis reports generated for real trading accounts. They represent actual performance, not backtested or simulated results.
            </p>
            
            <div className="view-full-report">
              <a 
                href={process.env.PUBLIC_URL + '/trading_analysis_report.html'} 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn btn-primary btn-large"
              >
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: '8px' }}>
                  <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 13H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M16 17H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M10 9H9H8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                View Full 15-Day Trading Report
              </a>              
            </div>
          </div>
        </div>
      </section>
      
      <section className="contact-options meeting-focus">
        <div className="container">
          <div className="booking-header">
            <h2>Schedule a <span className="highlight">Personal Consultation</span></h2>
            <p>Speak directly with our expert team to explore how Price on Rails can transform your trading strategy</p>
          </div>
          <div className="booking-card">
            <div className="booking-card-content">
              <div className="booking-icon">
                <svg width="64" height="64" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M36.6 30.52C35.1 29.02 33.28 29.02 31.8 30.52L28.68 33.64C26.1 32.32 23.7 30.42 21.56 28.26C19.58 26.28 17.68 23.9 16.36 21.32L19.48 18.2C20.98 16.72 20.98 14.9 19.48 13.4L13.4 7.32C11.92 5.84 10.1 5.84 8.6 7.32L5.08 10.86C3.68 12.26 3.12 14.2 3.48 16.08C4.32 20.52 6.94 26.14 11.4 30.6C15.86 35.06 21.48 37.68 25.92 38.52C26.36 38.6 26.8 38.64 27.22 38.64C28.78 38.64 30.26 38.04 31.38 36.92L34.9 33.4C36.38 31.9 36.38 30.08 34.9 28.6L36.6 30.52Z" fill="#2563EB"/>
                </svg>
              </div>
              <h3>Personalized Video Call</h3>
              <div className="booking-features">
                <div className="feature-item">
                  <span className="feature-icon">✓</span>
                  <span>One-on-one consultation</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">✓</span>
                  <span>Live platform demonstration</span>
                </div>
                <div className="feature-item">
                  <span className="feature-icon">✓</span>
                  <span>30-minute dedicated session</span>
                </div>
              </div>
              <div className="booking-cta">
                <a href="https://calendly.com/kevin-translatestocks/30min" className="btn-booking">
                  Schedule Your Call
                </a>
                <p className="booking-note">Choose a time that works for you</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <section className="trading-benefits">
        <div className="container">
          <div className="section-header center">
            <h2>Why Traders Are <span className="highlight">Abandoning</span> Traditional Methods</h2>
            <p>
              As more traders discover the AI Vibe Trading advantage, conventional trading approaches are becoming obsolete. Here's why the smartest traders are making the switch.
            </p>
          </div>
          
          <div className="comparison-table">
            <div className="comparison-header">
              <div className="comparison-cell header-cell">Features</div>
              <div className="comparison-cell header-cell traditional">Traditional Trading</div>
              <div className="comparison-cell header-cell por">AI Vibe Trading</div>
            </div>
            
            <div className="comparison-row">
              <div className="comparison-feature">Entry Strategy</div>
              <div className="comparison-cell traditional">Subjective pattern recognition</div>
              <div className="comparison-cell por">AI-powered intuitive trading with statistical validation</div>
            </div>
            
            <div className="comparison-row">
              <div className="comparison-feature">Win Rate</div>
              <div className="comparison-cell traditional">40-60% with extensive practice</div>
              <div className="comparison-cell por">78% proven win rate</div>
            </div>
            
            <div className="comparison-row">
              <div className="comparison-feature">Risk Management</div>
              <div className="comparison-cell traditional">Manual, emotion-influenced decisions</div>
              <div className="comparison-cell por">Automated, mathematically optimized targets</div>
            </div>
            
            <div className="comparison-row">
              <div className="comparison-feature">Market Analysis</div>
              <div className="comparison-cell traditional">Hours of chart study and guesswork</div>
              <div className="comparison-cell por">Instant AI analysis while you trade on vibes</div>
            </div>
            
            <div className="comparison-row">
              <div className="comparison-feature">Learning Curve</div>
              <div className="comparison-cell traditional">Years to master</div>
              <div className="comparison-cell por">Days to implement effectively</div>
            </div>
          </div>
        </div>
      </section>      
      
      <section className="video-demonstrations" id="video-demo">
        <div className="container">
          <div className="section-header center">
            <h2>Watch AI Vibe Trading <span className="highlight">In Action</span></h2>
            <p>
              See our technology working in real-time across different market conditions and timeframes. These videos showcase exactly how AI Vibe Trading with Price on Rails identifies high-probability trades.
            </p>
          </div>
          
          <div className="video-grid">
            <div className="video-item">
              <div className="video-wrapper">
                <iframe 
                  width="560" 
                  height="315" 
                  src="https://www.youtube.com/embed/NBfgxOKa2nY" 
                  title="Avoiding Common Trading Mistakes with Price on Rails" 
                  frameBorder="0" 
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                  allowFullScreen
                ></iframe>
              </div>
              <div className="video-description">
                <h3>AI Vibe Trading: 6 Hours in 3 Minutes</h3>
                <p>
                  Watch our autonomous AI trading system navigate markets with superhuman precision in this uncut time-lapse. This video condenses 6 continuous hours of AI Vibe Trading—not simulations or backtests. See how our technology projects price moves before they happen, executes at optimal points, and adapts to changing conditions, all while you provide high-level guidance based on market vibes.
                </p>
              </div>
            </div>
            
            <div className="video-item">
              <div className="video-wrapper">
                <iframe 
                  width="560" 
                  height="315" 
                  src="https://www.youtube.com/embed/kr6BVvdbitE" 
                  title="Price on Rails Trading Strategy Demonstration" 
                  frameBorder="0" 
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                  allowFullScreen
                ></iframe>
              </div>
              <div className="video-description">
                <h3>The AI Vibe Trading Crystal Ball</h3>
                <p>
                  Our "chart autocomplete" feature is the essence of AI Vibe Trading—instantly projecting the next 5 bars on any chart using historical data. Watch how this AI-driven tool acts like a "crystal ball" for short-term trend analysis, offering real-time visual cues on where the price might be headed next, letting you trade on intuition with AI-powered validation.
                </p>
              </div>
            </div>
            
            <div className="video-item">
              <div className="video-wrapper">
                <iframe 
                  width="560" 
                  height="315" 
                  src="https://www.youtube.com/embed/bAnEdY2Hq0Y" 
                  title="Real-Time Market Analysis with Price on Rails" 
                  frameBorder="0" 
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
                  allowFullScreen
                ></iframe>
              </div>
              <div className="video-description">
                <h3>98.11% Win Rate: AI Vibe Trading Mastery</h3>
                <p>
                  Witness our AI Vibe Trading platform achieve an astonishing 98.11% win rate—104 winning trades out of 106 executed in a single day! This video showcases how trading on vibes with AI support predicts market moves on Micro S&P 500 Futures, strategically manages positions, and patiently waits for the perfect moment to maximize profits.
                </p>
              </div>
            </div>
            
            <div className="youtube-channel-card">
              <div className="youtube-logo">                
                <svg xmlns="http://www.w3.org/2000/svg" height="800" width="1200" xmlSpace="preserve" y="0" x="0" id="Layer_1" version="1.1" viewBox="-57.15 -21.25 495.3 127.5">
                  <style id="style7427" type="text/css">{`.st2{fill:#282828}`}</style>
                  <g id="g7433">
                    <path id="path7429" d="M118.9 13.3c-1.4-5.2-5.5-9.3-10.7-10.7C98.7 0 60.7 0 60.7 0s-38 0-47.5 2.5C8.1 3.9 3.9 8.1 2.5 13.3 0 22.8 0 42.5 0 42.5s0 19.8 2.5 29.2C3.9 76.9 8 81 13.2 82.4 22.8 85 60.7 85 60.7 85s38 0 47.5-2.5c5.2-1.4 9.3-5.5 10.7-10.7 2.5-9.5 2.5-29.2 2.5-29.2s.1-19.8-2.5-29.3z" style={{fill: "red"}}/>
                    <path id="polygon7431" style={{fill: "#fff"}} d="M48.6 24.3v36.4l31.6-18.2z"/>
                  </g>
                  <g id="g7451">
                    <g id="g7449">
                      <path id="path7435" d="M176.3 77.4c-2.4-1.6-4.1-4.1-5.1-7.6-1-3.4-1.5-8-1.5-13.6v-7.7c0-5.7.6-10.3 1.7-13.8 1.2-3.5 3-6 5.4-7.6 2.5-1.6 5.7-2.4 9.7-2.4 3.9 0 7.1.8 9.5 2.4 2.4 1.6 4.1 4.2 5.2 7.6 1.1 3.4 1.7 8 1.7 13.8v7.7c0 5.7-.5 10.2-1.6 13.7-1.1 3.4-2.8 6-5.2 7.6-2.4 1.6-5.7 2.4-9.8 2.4-4.2-.1-7.6-.9-10-2.5zm13.5-8.4c.7-1.7 1-4.6 1-8.5V43.9c0-3.8-.3-6.6-1-8.4-.7-1.8-1.8-2.6-3.5-2.6-1.6 0-2.8.9-3.4 2.6-.7 1.8-1 4.6-1 8.4v16.6c0 3.9.3 6.8 1 8.5.6 1.7 1.8 2.6 3.5 2.6 1.6 0 2.7-.8 3.4-2.6z" className="st2"/>
                      <path id="path7437" d="M360.9 56.3V59c0 3.4.1 6 .3 7.7.2 1.7.6 3 1.3 3.7.6.8 1.6 1.2 3 1.2 1.8 0 3-.7 3.7-2.1.7-1.4 1-3.7 1.1-7l10.3.6c.1.5.1 1.1.1 1.9 0 4.9-1.3 8.6-4 11-2.7 2.4-6.5 3.6-11.4 3.6-5.9 0-10-1.9-12.4-5.6-2.4-3.7-3.6-9.4-3.6-17.2v-9.3c0-8 1.2-13.8 3.7-17.5 2.5-3.7 6.7-5.5 12.6-5.5 4.1 0 7.3.8 9.5 2.3 2.2 1.5 3.7 3.9 4.6 7 .9 3.2 1.3 7.6 1.3 13.2v9.1h-20.1zm1.5-22.4c-.6.8-1 2-1.2 3.7-.2 1.7-.3 4.3-.3 7.8v3.8h8.8v-3.8c0-3.4-.1-6-.3-7.8-.2-1.8-.7-3-1.3-3.7-.6-.7-1.6-1.1-2.8-1.1-1.4-.1-2.3.3-2.9 1.1z" className="st2"/>
                      <path id="path7439" d="M147.1 55.3L133.5 6h11.9l4.8 22.3c1.2 5.5 2.1 10.2 2.7 14.1h.3c.4-2.8 1.3-7.4 2.7-14l5-22.4h11.9L159 55.3v23.6h-11.8V55.3z" className="st2"/>
                      <path id="path7441" d="M241.6 25.7V79h-9.4l-1-6.5h-.3c-2.5 4.9-6.4 7.4-11.5 7.4-3.5 0-6.1-1.2-7.8-3.5-1.7-2.3-2.5-5.9-2.5-10.9V25.7h12v39.1c0 2.4.3 4.1.8 5.1s1.4 1.5 2.6 1.5c1 0 2-.3 3-1 1-.6 1.7-1.4 2.1-2.4V25.7z" className="st2"/>
                      <path id="path7443" d="M303.1 25.7V79h-9.4l-1-6.5h-.3c-2.5 4.9-6.4 7.4-11.5 7.4-3.5 0-6.1-1.2-7.8-3.5-1.7-2.3-2.5-5.9-2.5-10.9V25.7h12v39.1c0 2.4.3 4.1.8 5.1s1.4 1.5 2.6 1.5c1 0 2-.3 3-1 1-.6 1.7-1.4 2.1-2.4V25.7z" className="st2"/>
                      <path id="path7445" d="M274.2 15.7h-11.9v63.2h-11.7V15.7h-11.9V6h35.5z" className="st2"/>
                      <path id="path7447" d="M342.8 34.2c-.7-3.4-1.9-5.8-3.5-7.3s-3.9-2.3-6.7-2.3c-2.2 0-4.3.6-6.2 1.9-1.9 1.2-3.4 2.9-4.4 4.9h-.1V3.3h-11.6v75.6h9.9l1.2-5h.3c.9 1.8 2.3 3.2 4.2 4.3 1.9 1 3.9 1.6 6.2 1.6 4.1 0 7-1.9 8.9-5.6 1.9-3.7 2.9-9.6 2.9-17.5v-8.4c-.1-6.1-.4-10.8-1.1-14.1zm-11 21.7c0 3.9-.2 6.9-.5 9.1-.3 2.2-.9 3.8-1.6 4.7-.8.9-1.8 1.4-3 1.4-1 0-1.9-.2-2.7-.7-.8-.5-1.5-1.2-2-2.1V38.1c.4-1.4 1.1-2.6 2.1-3.6 1-.9 2.1-1.4 3.2-1.4 1.2 0 2.2.5 2.8 1.4.7 1 1.1 2.6 1.4 4.8.3 2.3.4 5.5.4 9.6v7z" className="st2"/>
                    </g>
                  </g>
                </svg>
              </div>
              <div className="youtube-content">
                <h3>Join Our AI Vibe Trading Community</h3>
                <p>
                  Subscribe to our YouTube channel for more AI Vibe Trading videos, tutorials, and live trading sessions. Get free insights, market analysis, and trading education directly from our experts.
                </p>
                <a href="https://youtube.com/@translatestocks?sub_confirmation=1" className="youtube-btn" target="_blank" rel="noopener noreferrer">
                  <span className="youtube-icon">
                  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 576 512" fill="currentColor">
                    <path d="M549.7 124.1c-6.3-24-25-42.7-49-49.1C456.5 64 288 64 288 64s-38 0-47.5 2.5C8.1 3.9 3.9 8.1 2.5 13.3 0 22.8 0 42.5 0 42.5s0 19.8 2.5 29.2C3.9 76.9 8 81 13.2 82.4 22.8 85 60.7 85 60.7 85s38 0 47.5-2.5c5.2-1.4 9.3-5.5 10.7-10.7 2.5-9.5 2.5-29.2 2.5-29.2s.1-19.8-2.5-29.3zM232 336V176l142 80-142 80z"/>
                  </svg>
                  </span>
                  Subscribe Now
                </a>
              </div>
            </div>
          </div>
          
        </div>
      </section>
      
      <section className="founder-journey">
        <div className="container">
          <div className="section-header center">
            <span className="overline">THE REAL ORIGIN STORY</span>
            <h2>From <span className="highlight">Market Disruption</span> to <span className="highlight-alt">AI Vibe Trading Revolution</span></h2>
            <p className="section-description">
              Great innovations often emerge from unexpected challenges. The AI Vibe Trading story reveals how market volatility sparked a revolutionary trading approach powered by our Price on Rails technology.
            </p>
          </div>

          <div className="timeline-container">
            <table className="story-timeline">
              <tbody>
                <tr className="timeline-item">
                  <td className="timeline-content">
                    <div className="content-wrapper">
                      <h3>The Fed's Unexpected Move</h3>
                      <p>
                        In early 2024, a significant shift occurred in market volatility when the Federal Reserve unexpectedly delayed actions to adjust the fed funds rate. This unprecedented move triggered substantial drawdowns in some of our trading algorithms, which relied heavily on predictable interest rate forecasts.
                      </p>
                      <p className="quote">
                        What seemed like a market disruption became the catalyst for an entirely new approach to trading technology.
                      </p>
                    </div>
                  </td>
                  <td className="timeline-illustration">
                    <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" className="timeline-svg">
                      <path fill="#00694A" fillOpacity="0.2" d="M45.3,-50.9C56.9,-35.9,63.2,-18,65.7,2.5C68.2,22.9,66.8,45.9,55.2,60.5C43.6,75.2,21.8,81.6,0.2,81.4C-21.4,81.2,-42.9,74.5,-56.4,59.7C-70,44.9,-75.7,22.5,-74.5,1.2C-73.2,-20.1,-65.1,-40.2,-51.1,-55.2C-37,-70.2,-18.5,-80.2,-0.3,-79.9C17.9,-79.6,35.7,-69.1,45.3,-50.9Z" transform="translate(100 100)" />
                      <path fill="none" stroke="#00694A" strokeWidth="2" d="M40,120 L160,120 M60,80 L140,80 M80,140 L120,140 M100,60 L100,160" />
                      <circle cx="100" cy="100" r="8" fill="#00694A" />
                      <circle cx="100" cy="140" r="5" fill="#00694A" opacity="0.7" />
                      <circle cx="140" cy="120" r="5" fill="#00694A" opacity="0.7" />
                      <circle cx="60" cy="120" r="5" fill="#00694A" opacity="0.7" />
                    </svg>
                  </td>
                </tr>

                <tr className="timeline-item">
                  <td className="timeline-content">
                    <div className="content-wrapper">
                      <h3>The TGT4 Project Breakthrough</h3>
                      <p>
                        To tackle this challenge, we initiated a groundbreaking project named TGT4, leveraging advanced Transformers AI to predict market movements in real-time. As the TGT4 model evolved, we shared an early version with our clients, featuring a visual forecasting interface instead of our usual closed algorithmic approach.
                      </p>
                      <p className="quote">
                        The initial intention was purely demonstrative—to illustrate the direction and capability of our emerging models. We had no idea this visualization would become the most powerful trading tool in our arsenal.
                      </p>
                    </div>
                  </td>
                  <td className="timeline-illustration">
                    <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" className="timeline-svg">
                      <path fill="#00694A" fillOpacity="0.2" d="M39.9,-45.7C51.3,-34.1,60,-20.4,63.8,-4.8C67.7,10.8,66.7,28.2,57.6,39.2C48.5,50.3,31.2,54.8,14.4,58.3C-2.5,61.8,-18.8,64.2,-32.2,58.2C-45.5,52.1,-56,37.6,-60.5,21.3C-65,5,-63.6,-13.2,-55.5,-26.2C-47.4,-39.2,-32.5,-47.1,-18,-52.5C-3.6,-57.9,10.5,-60.8,23.3,-57.2C36.1,-53.6,48.5,-43.4,39.9,-45.7Z" transform="translate(100 100)" />
                      <line x1="50" y1="150" x2="70" y2="130" stroke="#00694A" strokeWidth="2"/>
                      <line x1="70" y1="130" x2="90" y2="110" stroke="#00694A" strokeWidth="2"/>
                      <line x1="90" y1="110" x2="110" y2="130" stroke="#00694A" strokeWidth="2"/>
                      <line x1="110" y1="130" x2="130" y2="90" stroke="#00694A" strokeWidth="2"/>
                      <line x1="130" y1="90" x2="150" y2="70" stroke="#00694A" strokeWidth="2"/>
                      <circle cx="50" cy="150" r="4" fill="#00694A"/>
                      <circle cx="70" cy="130" r="4" fill="#00694A"/>
                      <circle cx="90" cy="110" r="4" fill="#00694A"/>
                      <circle cx="110" cy="130" r="4" fill="#00694A"/>
                      <circle cx="130" cy="90" r="4" fill="#00694A"/>
                      <circle cx="150" cy="70" r="4" fill="#00694A"/>
                      <path fill="none" stroke="#FF6B00" strokeWidth="2" strokeDasharray="5,5" d="M50,150 Q100,50 150,70"/>
                    </svg>
                  </td>
                </tr>

                <tr className="timeline-item">
                  <td className="timeline-content">
                    <div className="content-wrapper">
                      <h3>The Unexpected Discovery</h3>
                      <p>
                        Something remarkable and entirely unforeseen happened. Traders began actively using these graphical predictions in their trading decisions and soon reported exceptional success rates. The chart provided forecasts for the subsequent five intraday bars, becoming a highly effective trading aid.
                      </p>
                      <p className="quote">
                        What was intended as a mere demonstration of our AI's potential had accidentally revealed a powerful trading edge that anyone could use.
                      </p>
                    </div>
                  </td>
                  <td className="timeline-illustration">
                    <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" className="timeline-svg">
                      <path fill="#00694A" fillOpacity="0.2" d="M44.7,-64.1C57.8,-55.1,68.2,-41.5,74.9,-25.9C81.6,-10.2,84.6,7.5,79.8,22.4C75,37.3,62.5,49.4,48.3,58.5C34,67.7,17,73.8,0,73.8C-17,73.8,-34.1,67.7,-45.7,56.8C-57.2,45.9,-63.3,30.1,-67.5,13.2C-71.7,-3.6,-74,-21.6,-67.4,-35.2C-60.8,-48.8,-45.3,-58,-30.1,-67.2C-15,-76.4,-0.1,-85.5,13.7,-83C27.5,-80.4,55,-81.2,44.7,-64.1Z" transform="translate(100 100)" />
                      <rect x="40" y="60" width="120" height="80" rx="10" fill="white" stroke="#00694A" strokeWidth="2"/>
                      <line x1="50" y1="110" x2="70" y2="90" stroke="#00694A" strokeWidth="2"/>
                      <line x1="70" y1="90" x2="90" y2="100" stroke="#00694A" strokeWidth="2"/>
                      <line x1="90" y1="100" x2="110" y2="80" stroke="#00694A" strokeWidth="2"/>
                      <line x1="110" y1="80" x2="130" y2="95" stroke="#00694A" strokeWidth="2"/>
                      <line x1="130" y1="95" x2="150" y2="75" stroke="#00694A" strokeWidth="2"/>
                      <path fill="none" stroke="#FF6B00" strokeWidth="2" strokeDasharray="5,5" d="M110,80 Q130,60 150,75"/>
                      <circle cx="50" cy="110" r="3" fill="#00694A"/>
                      <circle cx="70" cy="90" r="3" fill="#00694A"/>
                      <circle cx="90" cy="100" r="3" fill="#00694A"/>
                      <circle cx="110" cy="80" r="3" fill="#00694A"/>
                      <circle cx="130" cy="95" r="3" fill="#00694A"/>
                      <circle cx="150" cy="75" r="3" fill="#00694A"/>
                    </svg>
                  </td>
                </tr>

                <tr className="timeline-item">
                  <td className="timeline-content">
                    <div className="content-wrapper">
                      <h3>From 'Too Good to Be True' to Reality</h3>
                      <p>
                        Encouraged by this unexpected success, we delved deeper into these predictive patterns. Soon after, we refined our visualization approach, normalizing predictions against the last bar's opening price. This refined chart, tested in live trading environments, delivered astonishing results: more than 99% of trades were profitable, and every single trading session ended positively.
                      </p>
                      <p>
                        These results aren't just claims—they're documented. All original live trading sessions, recorded and published on our YouTube channel and our broker partners' channels, show a 100% success rate through the end of March 2025. Some days demonstrated exceptional profits, while even on challenging market days, sessions still closed with positive results—not a single losing day.
                      </p>
                      <p className="investor-highlight">
                        <span className="investor-icon">
                          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" />
                          </svg>
                        </span>
                        What followed was unprecedented: customers of our original trading algorithms, astonished by the Price on Rails results, approached us with investment offers. These same clients who had experienced the technology firsthand became the earliest investors in TranslateStocks.com, providing the capital and market validation needed to bring this revolutionary approach to the wider trading community.
                      </p>
                      <p className="quote">
                        What initially appeared "too good to be true" proved to be genuinely transformative. Realizing the immense potential and value for traders, we shifted our mission to make this powerful forecasting tool accessible to everyone. And so, Price on Rails—our revolutionary predictive trading platform—was officially born.
                      </p>
                    </div>
                  </td>
                  <td className="timeline-illustration">
                    <svg viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" className="timeline-svg">
                      <path fill="#00694A" fillOpacity="0.2" d="M38.5,-48.1C54.6,-44.7,75.5,-39.5,81.8,-27.5C88.1,-15.4,79.9,3.5,72.2,20.5C64.4,37.5,57.2,52.6,44.9,62.1C32.5,71.6,16.3,75.6,0.5,74.9C-15.2,74.3,-30.4,69,-40.8,58.8C-51.2,48.7,-56.7,33.7,-63.2,17.7C-69.7,1.7,-77.1,-15.2,-73.2,-28.5C-69.2,-41.7,-53.8,-51.3,-39.1,-55.3C-24.5,-59.3,-10.7,-57.9,1.4,-60C13.6,-62.1,27.2,-67.9,38.5,-48.1Z" transform="translate(100 100)" />
                      <circle cx="100" cy="100" r="50" fill="none" stroke="#00694A" strokeWidth="2"/>
                      <text x="100" y="105" textAnchor="middle" fill="#00694A" fontWeight="bold" fontSize="24">100%</text>
                      <path d="M75,70 C80,50 120,50 125,70" fill="none" stroke="#00694A" strokeWidth="2"/>
                      <path d="M75,140 A50,50 0 0,0 125,140" fill="none" stroke="#00694A" strokeWidth="2"/>
                      <circle cx="75" cy="100" r="5" fill="#00694A"/>
                      <circle cx="125" cy="100" r="5" fill="#00694A"/>
                      <text x="100" y="130" textAnchor="middle" fill="#00694A" fontSize="12">VERIFIED ON YOUTUBE</text>
                    </svg>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="transformation-stories">
            <h3>The Transformation <span className="highlight">We've Witnessed</span></h3>
            
            <div className="transformation-grid">
              <div className="transformation-card">
                <div className="before-after">
                  <div className="before">
                    <h4>Before</h4>
                    <p>Trading blindly during Fed announcements with high uncertainty and risk</p>
                  </div>
                  <div className="arrow">→</div>
                  <div className="after">
                    <h4>After</h4>
                    <p>Confidently navigating market volatility with AI-powered price projections</p>
                  </div>
                </div>
              </div>
              
              <div className="transformation-card">
                <div className="before-after">
                  <div className="before">
                    <h4>Before</h4>
                    <p>Relying on traditional indicators that lag behind fast-moving markets</p>
                  </div>
                  <div className="arrow">→</div>
                  <div className="after">
                    <h4>After</h4>
                    <p>Trading with prescient forecasts of the next five price bars in real-time</p>
                  </div>
                </div>
              </div>
              
              <div className="transformation-card">
                <div className="before-after">
                  <div className="before">
                    <h4>Before</h4>
                    <p>Inconsistent trading results with significant drawdowns during volatility</p>
                  </div>
                  <div className="arrow">→</div>
                  <div className="after">
                    <h4>After</h4>
                    <p>Achieving 100% profitable sessions with documented results on YouTube through March 2025</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="founder-cta">
            <p>
              What began as a response to market disruption has evolved into a trading revolution. Price on Rails isn't just another indicator—it's a glimpse into the future of price movement.
            </p>
            <Link to="/pricing" className="btn btn-primary btn-large">
              Experience the Revolution
            </Link>
          </div>
        </div>
      </section>
      
      <section className="ai-agent-explanation">
        <div className="container">
          <div className="section-header center">
            <span className="overline">UNDERSTANDING OUR TECHNOLOGY</span>
            <h2>What is <span className="highlight">Vibe Trading</span> with an AI Agent?</h2>
            <p className="section-description">
              Vibe Trading combines your intuitive market sense with our AI's statistical precision. You provide the high-level direction while our AI handles the complex analysis and execution details.
            </p>
          </div>

          <div className="ai-agent-content">
            <div className="ai-agent-grid">
              <div className="ai-agent-definition">
                <h3>The Technology Behind Vibe Trading</h3>
                <p>
                  Vibe Trading is powered by our Price on Rails technology - a sophisticated software that runs locally on your server (which we provide with our subscription), using your market data connections to analyze trading conditions in real-time. Unlike traditional trading systems, our technology combines artificial intelligence with advanced statistical modeling to identify and execute high-probability trades.
                </p>
                <p>
                  Like "vibe coding" where AI handles the complex programming details, Vibe Trading lets you trade based on your market intuition while our AI handles the complex analysis, timing, and risk management behind the scenes. You maintain complete control and can override any decision at any time.
                </p>
              </div>

              <div className="compliance-info">
                <h3>Important Regulatory Compliance Information</h3>
                <div className="compliance-list">
                  <div className="compliance-item">
                    <div className="compliance-icon negative">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#E53E3E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M15 9L9 15" stroke="#E53E3E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M9 9L15 15" stroke="#E53E3E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <p><strong>We are NOT Financial Advisors:</strong> Translate Stocks is not registered as an investment advisor with the SEC or any state securities regulator.</p>
                  </div>

                  <div className="compliance-item">
                    <div className="compliance-icon negative">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#E53E3E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M15 9L9 15" stroke="#E53E3E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M9 9L15 15" stroke="#E53E3E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <p><strong>We are NOT Trading on Your Behalf:</strong> We do not access your brokerage accounts or trade on behalf of investors.</p>
                  </div>

                  <div className="compliance-item">
                    <div className="compliance-icon negative">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#E53E3E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M15 9L9 15" stroke="#E53E3E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M9 9L15 15" stroke="#E53E3E" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <p><strong>We are NOT Signal Providers:</strong> We do not operate server-side trading signals or distribute the same trading recommendations to multiple users.</p>
                  </div>

                  <div className="compliance-item">
                    <div className="compliance-icon positive">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18456 2.99721 7.13638 4.39828 5.49707C5.79935 3.85776 7.69279 2.71793 9.79619 2.24013C11.8996 1.76233 14.1003 1.98003 16.07 2.86" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                        <path d="M22 4L12 14.01L9 11.01" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      </svg>
                    </div>
                    <p><strong>We ARE a SaaS Provider:</strong> We provide cloud-based computing resources and software tools for individual traders to make their own trading decisions.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="individual-results-disclaimer">
              <h3>Why Results Will Vary Between Users</h3>
              <p>
                Even if two traders use identical settings with our AI Agent, they will experience different trading results. This is because:
              </p>
              <ul className="results-factors">
                <li>Each user has unique network latency and hardware performance factors</li>
                <li>Market data feeds have microsecond differences in timing and price reporting</li>
                <li>Local AI processing creates unique analysis patterns for each user's environment</li>
                <li>Different brokers have varying execution speeds and price improvements</li>
              </ul>
              <div className="analogy-box">
                <h4>Think of it this way:</h4>
                <p>
                  If two hedge funds hired identical twins who graduated from the same Ivy League university with identical grades, would they make the exact same trades at their respective firms? Of course not. Despite similar training, each would operate in a different environment with unique information flows, leading to different (though perhaps similar) decisions.
                </p>
                <p>
                  Similarly, our AI Agent adapts to your specific trading environment, creating a truly personalized trading assistant rather than a one-size-fits-all signal service.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <section className="avoid-mistakes-section">
        <div className="container">
          <div className="section-header center">
            <span className="overline">AVOID COSTLY ERRORS</span>
            <h2>Escape the <span className="highlight">"Revenge Trading"</span> Trap</h2>
            <p className="section-description">
              Even experienced traders know the pain of "revenge trading", when emotions take over, leading to account-destroying losses. Price on Rails' statistical approach eliminates the emotional triggers that cause these devastating days.
            </p>
          </div>

          <div className="trading-mistakes-grid">
            <div className="trading-mistakes-cards">
              <div className="mistake-card">
                <div className="mistake-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M15 9L9 15" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M9 9L15 15" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h4>Revenge Trading After a Loss</h4>
                <p>Attempting to recover losses with impulsive, high-risk trades often leads to compounding losses and emotional trading decisions.</p>
              </div>

              <div className="mistake-card">
                <div className="mistake-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M15 9L9 15" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M9 9L15 15" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h4>Averaging Down on Losing Positions</h4>
                <p>Adding to losing positions without statistical edge can amplify losses and lead to account-destroying drawdowns.</p>
              </div>

              <div className="mistake-card">
                <div className="mistake-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M15 9L9 15" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M9 9L15 15" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h4>Trading During High-Volatility Periods</h4>
                <p>Attempting to trade during extreme market volatility often leads to slippage, poor executions, and unexpected losses.</p>
              </div>

              <div className="mistake-card">
                <div className="mistake-icon">
                  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M15 9L9 15" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M9 9L15 15" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h4>Ignoring Statistical Probabilities</h4>
                <p>Making decisions based on emotions rather than mathematical edge leads to inconsistent results and poor long-term performance.</p>
              </div>
            </div>

            <div className="solution-panel">
              <div className="solution-header">
                <div className="solution-icon">
                  <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 11.08V12C21.9988 14.1564 21.3005 16.2547 20.0093 17.9818C18.7182 19.709 16.9033 20.9725 14.8354 21.5839C12.7674 22.1953 10.5573 22.1219 8.53447 21.3746C6.51168 20.6273 4.78465 19.2461 3.61096 17.4371C2.43727 15.628 1.87979 13.4881 2.02168 11.3363C2.16356 9.18456 2.99721 7.13638 4.39828 5.49707C5.79935 3.85776 7.69279 2.71793 9.79619 2.24013C11.8996 1.76233 14.1003 1.98003 16.07 2.86" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M22 4L12 14.01L9 11.01" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </div>
                <h3>Price on Rails Solution</h3>
              </div>
              <p className="solution-description">
                Our system keeps you out of the market during high-risk periods and uses statistical validation to prevent emotional decision-making. The result? No more account-destroying days.
              </p>
              <div className="trading-advice-grid">
                <div className="advice-item">
                  <h4>Do Not Trade Openings</h4>
                </div>
                <div className="advice-item">
                  <h4>Do Not Trade High Volatility Events or Earning Seasons</h4>
                </div>
                <div className="advice-item">
                  <h4>Run Away From Atypical High Volumes</h4>
                </div>
                <div className="advice-item">
                  <h4>Avoid Trading 1 Day Before or After Holidays</h4>
                </div>
              </div>
              <Link to="/pricing" className="btn btn-primary">
                Price on Rails
              </Link>
            </div>
          </div>
        </div>
      </section>
      
      <section className="testimonials">
        <div className="container">
          <div className="section-header center">
            <h2>Traders Who <span className="highlight">Changed Their Lives</span> with Price on Rails</h2>
            <p>
              Don't just take our word for it. Here's what real traders are saying about Price on Rails.
            </p>
          </div>
          
          <div className="testimonial-grid">
            <div className="testimonial-card">
              <div className="testimonial-content">
                <p>
                  "After 5 years of inconsistent trading, Price on Rails completely transformed my approach. The 78% win rate is undeniable, I'm finally profitable month after month."
                </p>
              </div>
              <div className="testimonial-author">
                <img 
                  src="/images/testimonial-1.jpg" 
                  alt="Michael R." 
                  width="60" 
                  height="60"
                  loading="lazy"
                />
                <div>
                  <h4>Michael R.</h4>
                  <p>Former Day Trader, Now Full-Time Investor</p>
                </div>
              </div>
            </div>
            
            <div className="testimonial-card">
              <div className="testimonial-content">
                <p>
                  "I was skeptical about AI in trading, but Price on Rails proved me wrong. The confluence points it identifies are uncanny. I've reduced my trading time by 70% while increasing profits."
                </p>
              </div>
              <div className="testimonial-author">
                <img 
                  src="/images/testimonial-2.jpg" 
                  alt="Sarah K." 
                  width="60" 
                  height="60"
                  loading="lazy"
                />
                <div>
                  <h4>Sarah K.</h4>
                  <p>Professional Futures Trader</p>
                </div>
              </div>
            </div>
            
            <div className="testimonial-card">
              <div className="testimonial-content">
                <p>
                  "The 'Fury Day' protection alone is worth the price. Before Price on Rails, I'd regularly blow up accounts. Now I haven't had a significant drawdown in 8 months."
                </p>
              </div>
              <div className="testimonial-author">
                <img 
                  src="/images/testimonial-3.jpg" 
                  alt="David T." 
                  width="60" 
                  height="60"
                  loading="lazy"
                />
                <div>
                  <h4>David T.</h4>
                  <p>Cryptocurrency and Equity Trader</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2>While Others Guess, You Vibe Trade With Confidence</h2>
            <p>
              Every day that passes without Vibe Trading is another day trading without your statistical edge. Top traders are already using it, shouldn't you?
            </p>
            <div className="cta-buttons">
              <Link to="/pricing" className="btn btn-primary btn-large">
                Start Vibe Trading
              </Link>
              <div className="guarantee">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9 12L11 14L15 10" stroke="#4CAF50" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Unique Tool</span>
              </div>
              <div className="guarantee patent">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 15C13.6569 15 15 13.6569 15 12C15 10.3431 13.6569 9 12 9C10.3431 9 9 10.3431 9 12C9 13.6569 10.3431 15 12 15Z" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M20 12H21" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M3 12H4" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 20V21" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M12 3V4" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M17.657 17.6569L18.364 18.364" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M5.63599 5.63605L6.34309 6.34315" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M17.657 6.34315L18.364 5.63605" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M5.63599 18.364L6.34309 17.6569" stroke="#2563EB" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                <span>Patent Protected</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default TradingEdge;